import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Field, ErrorMessage } from "formik";
import { Button, Form } from "react-bootstrap";
import ReactSelect from "react-select";
import { LoadingSpinner } from "../common/Loading/LoadingSpinner";
import { postcodeService } from "../../services";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { normalizePostcodeInput, toCamelCase } from "../../utils";
import {
  buildFieldPath,
  getPrefixedError,
  getPrefixedTouched,
  getPrefixedValue,
} from "./addressFieldPath";

export type PostcodeSelectorLayout = "below" | "separate";

export type PostcodeLookupOption = {
  value: string;
  label: string;
  address: Record<string, string>;
};

type PostcodeLookupContextValue = {
  isLooking: boolean;
  setIsLooking: React.Dispatch<React.SetStateAction<boolean>>;
  lookupOptions: PostcodeLookupOption[];
  setLookupOptions: React.Dispatch<React.SetStateAction<PostcodeLookupOption[]>>;
  selectedLookup: PostcodeLookupOption | null;
  setSelectedLookup: React.Dispatch<React.SetStateAction<PostcodeLookupOption | null>>;
};

const PostcodeLookupContext = createContext<PostcodeLookupContextValue | null>(null);

/** Preserves lookup/selection state when sibling layouts remount across breakpoints. */
export function AddressLookupProvider({ children }: { children: React.ReactNode }) {
  const [isLooking, setIsLooking] = useState(false);
  const [lookupOptions, setLookupOptions] = useState<PostcodeLookupOption[]>([]);
  const [selectedLookup, setSelectedLookup] = useState<PostcodeLookupOption | null>(null);

  const value: PostcodeLookupContextValue = {
    isLooking,
    setIsLooking,
    lookupOptions,
    setLookupOptions,
    selectedLookup,
    setSelectedLookup,
  };

  return <PostcodeLookupContext.Provider value={value}>{children}</PostcodeLookupContext.Provider>;
}

function usePostcodeLookupContext(): PostcodeLookupContextValue {
  const ctx = useContext(PostcodeLookupContext);
  if (!ctx) {
    throw new Error("PostcodeLookup must be used within AddressLookupProvider");
  }
  return ctx;
}

export interface PostcodeLookupProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: any;
  setFieldValue: any;
  setFieldError: any;
  setFieldTouched: any;
  setStatus: any;
  fieldPrefix?: string;
  onAddressSelected?: (address: any) => void;
  showLookupButton?: boolean;
  showAddressSelector?: boolean;
  required?: boolean;
  /**
   * `below`: postcode + button, selector underneath on md and below; all inline on lg+.
   * `separate`: postcode group and selector group stacked (narrow layouts).
   */
  selectorLayout?: PostcodeSelectorLayout;
  /** Applied to the postcode Form.Group (default `mb-3`). */
  className?: string;
}

export const PostcodeLookup: React.FC<PostcodeLookupProps> = ({
  values,
  errors,
  touched,
  setFieldValue,
  setFieldError,
  setFieldTouched,
  setStatus,
  fieldPrefix = "",
  onAddressSelected,
  showLookupButton = true,
  showAddressSelector = true,
  required = false,
  selectorLayout = "below",
  className = "mb-3",
}) => {
  const {
    isLooking,
    setIsLooking,
    lookupOptions,
    setLookupOptions,
    selectedLookup,
    setSelectedLookup,
  } = usePostcodeLookupContext();

  const lookupSelectRef = useRef<any>(null);

  useEffect(() => {
    if (lookupOptions.length > 0 && lookupSelectRef.current) {
      lookupSelectRef.current.focus();
    }
  }, [lookupOptions]);

  const getFieldName = (fieldName: string) => buildFieldPath(fieldPrefix, fieldName);
  const getFieldValue = (fieldName: string) => getPrefixedValue(values, fieldPrefix, fieldName) ?? "";
  const getFieldError = (fieldName: string) => getPrefixedError(errors, fieldPrefix, fieldName);
  const getFieldTouched = (fieldName: string) => getPrefixedTouched(touched, fieldPrefix, fieldName);

  const postcodeFieldName = getFieldName("postcode");
  const postcodeInvalid = getFieldError("postcode") && getFieldTouched("postcode");
  const premisesId = `${postcodeFieldName}-premises`;

  const mapPostcodeResponseToOptions = (response: {
    premises?: Array<Record<string, string>>;
    normalizedPostcode?: string;
  }): PostcodeLookupOption[] => {
    return (response?.premises ?? []).map((premise, index) => ({
      value: premise.udprn ?? String(index),
      label: premise.summaryLine ?? "",
      address: {
        addressLine1: premise.addressLine1 ?? "",
        addressLine2: premise.addressLine2 ?? "",
        addressLine3: premise.addressLine3 ?? "",
        town: premise.town ?? "",
        county: premise.county ?? "",
        postcode: premise.postcode ?? response.normalizedPostcode ?? "",
        country: premise.country ?? "United Kingdom",
      },
    }));
  };

  const lookUpPostCode = (postcode: string) => {
    if (!postcode.trim()) return;
    setIsLooking(true);
    postcodeService
      .lookup(postcode)
      .then((res) => {
        if (res.premises && res.premises.length > 0) {
          const options = mapPostcodeResponseToOptions(res);
          setLookupOptions(options);
          setSelectedLookup(null);
          setIsLooking(false);
        } else {
          setLookupOptions([]);
          setIsLooking(false);
          setStatus("No addresses found for this postcode.");
        }
      })
      .catch(() => {
        setLookupOptions([]);
        setIsLooking(false);
        setStatus("Postcode lookup failed.");
      });
  };

  const handleAddressSelection = (selectedOption: PostcodeLookupOption | null) => {
    if (!selectedOption) {
      setSelectedLookup(null);
      return;
    }

    const { address } = selectedOption;

    const applyField = (name: string, value: string) => {
      const fullName = buildFieldPath(fieldPrefix, name);
      setFieldValue(fullName, toCamelCase(value));
      setFieldError(fullName, "");
      setFieldTouched(fullName, false);
    };

    applyField("addressLine1", address.addressLine1 ?? "");
    applyField("addressLine2", address.addressLine2 ?? "");
    applyField("addressLine3", address.addressLine3 ?? "");
    applyField("town", address.town ?? "");
    applyField("county", address.county ?? "");
    applyField("postcode", normalizePostcodeInput(address.postcode ?? ""));
    applyField("country", address.country ?? "");

    setSelectedLookup(selectedOption);

    if (onAddressSelected) {
      onAddressSelected(address);
    }
  };

  const runLookup = () => {
    const postcode = getFieldValue("postcode");
    if (postcode && postcode.trim()) {
      lookUpPostCode(postcode);
    }
  };

  const postcodeLabel = (
    <>
      Postcode
      {required && (
        <>
          {" "}
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        </>
      )}
    </>
  );

  const postcodeInput = (
    <div style={{ maxWidth: "10rem", minWidth: "8rem" }}>
      <Field
        id={postcodeFieldName}
        name={postcodeFieldName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue(postcodeFieldName, normalizePostcodeInput(e.target.value));
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            e.preventDefault();
            runLookup();
          }
        }}
        type="text"
        value={normalizePostcodeInput(getFieldValue("postcode"))}
        className={`form-control text-uppercase${postcodeInvalid ? " is-invalid" : ""}`}
      />
      <ErrorMessage name={postcodeFieldName} component="div" className="invalid-feedback d-block" />
    </div>
  );

  const lookupButton = showLookupButton && (
    <Button
      variant="primary"
      type="button"
      onClick={runLookup}
      disabled={!getFieldValue("postcode") || !getFieldValue("postcode").trim() || isLooking}
    >
      {isLooking ? (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <LoadingSpinner text="Looking..." />
        </span>
      ) : (
        "Look Up"
      )}
    </Button>
  );

  const showSelector = showAddressSelector && lookupOptions.length > 0;

  const addressSelector = showSelector && (
    <div className="w-100 min-w-0 flex-grow-1" id={premisesId}>
      <ReactSelect
        ref={lookupSelectRef}
        value={selectedLookup}
        styles={ReactSelectBootstrapStyle}
        className="stateManagedSelect"
        options={lookupOptions}
        isClearable
        blurInputOnSelect
        closeMenuOnSelect
        onChange={handleAddressSelection}
        placeholder="Select an address…"
        aria-label="Select address"
      />
    </div>
  );

  if (selectorLayout === "separate") {
    return (
      <>
        <Form.Group className={className} controlId={postcodeFieldName}>
          <Form.Label>{postcodeLabel}</Form.Label>
          <div className="d-flex flex-wrap gap-2 align-items-start">{postcodeInput}{lookupButton}</div>
        </Form.Group>
        {showSelector && (
          <Form.Group className="mb-3">
            <Form.Label>Select address</Form.Label>
            {addressSelector}
          </Form.Group>
        )}
      </>
    );
  }

  return (
    <Form.Group className={className} controlId={postcodeFieldName}>
      <Form.Label>{postcodeLabel}</Form.Label>
      <div className="d-flex flex-column flex-lg-row gap-2 align-items-start w-100">
        <div className="d-flex flex-wrap gap-2 align-items-start flex-shrink-0">{postcodeInput}{lookupButton}</div>
        {addressSelector}
      </div>
    </Form.Group>
  );
};
